import { create } from 'zustand';

/**
 * The active task timer. Sections 1 and 2 of the request of 20 September 2026,
 * which reverse all three of the rules this store was built on.
 *
 * It used to measure ACTIVE time: it counted a second per tick, paused itself
 * after ninety seconds with no pointer, key, scroll or visibility change, and
 * allowed one task per person. Closing the browser therefore lost the time,
 * because nothing sent a pause and the count lived in this tab. The client
 * reported exactly that and asked for the opposite. So:
 *
 * 1. It is WALL CLOCK, from the server's `startedAt`. Close the browser, open it
 *    a day later, and the clock reads a day, because a day is what elapsed.
 * 2. Nothing stops it but Pause or Done. There is no idle rule. `IDLE_MS`,
 *    `pausedByIdle` and `noteInteraction` are gone rather than disabled: a dead
 *    rule left in the file is a rule somebody re-enables.
 * 3. SEVERAL tasks run at once. A ceramist with three trays in the furnace is
 *    working three cases, so this holds a map rather than one kit.
 *
 * Nothing is counted here. The store holds, per running kit, when the current
 * run began and what its history entry had already accumulated before an earlier
 * pause; `elapsedOf` computes the display from those and the shared clock. The
 * SERVER is the only source of the figure that reaches a report: it computes each
 * entry's seconds from its own timestamps, and no command carries an elapsed
 * count any more.
 *
 * What this costs is written down in section 1.3 and was put to the client: a
 * technician who forgets to press Pause at six in the evening has a fourteen hour
 * task the next morning, and utilisation would read over a hundred percent if the
 * report did not cap it.
 */

/** One task running on this device, as the server described it. */
export interface RunningTask {
  /** When the CURRENT run began, per the server. Wall clock, in epoch ms. */
  startedAt: number;
  /**
   * Seconds the open history entry already holds from earlier runs of the same
   * stage. Zero on a first start; non-zero only for a task that was paused and
   * taken up again.
   */
  accumulatedSeconds: number;
}

interface TimerState {
  /** Every task running for this person, by kit id. Section 2: there may be several. */
  running: Record<string, RunningTask>;
  /**
   * Wall clock, refreshed by the single application tick. Section 10 item 4
   * allows ONE interval for the whole application, so every clock on a screen
   * reads from here rather than starting a second one: this device's own tasks,
   * the live work chips on the floor board, and the running strip.
   */
  nowMs: number;
  /**
   * What each paused task's clock read when it was paused, by kit id, so the
   * clock stays at that time instead of falling back to zero. Request of
   * 5 October 2026. Starting the task again takes it from the server.
   */
  paused: Record<string, number>;

  /** A task has started, or been taken up again. */
  start: (kitId: string, task: RunningTask) => void;
  /** Replaces the whole set, for seeding from the server on load. */
  seed: (tasks: Record<string, RunningTask>) => void;
  /** This task has stopped. The others keep running. */
  stop: (kitId: string) => void;
  /** This task was paused: it stops, and its clock keeps the time it had reached. */
  pause: (kitId: string) => void;
  tick: () => void;
  /** Everything stops. Signing out, and nothing else. */
  clear: () => void;
}

export const useTimerStore = create<TimerState>((set) => ({
  running: {},
  nowMs: Date.now(),
  paused: {},

  start: (kitId, task) =>
    set((state) => {
      const { [kitId]: resumed, ...paused } = state.paused;
      void resumed;
      return { running: { ...state.running, [kitId]: task }, paused };
    }),

  seed: (tasks) => set({ running: tasks }),

  stop: (kitId) =>
    set((state) => {
      const { [kitId]: gone, ...rest } = state.running;
      void gone;
      return { running: rest };
    }),

  pause: (kitId) =>
    set((state) => {
      const task = state.running[kitId];
      const { [kitId]: gone, ...rest } = state.running;
      void gone;
      return {
        running: rest,
        paused:
          task === undefined
            ? state.paused
            : { ...state.paused, [kitId]: elapsedOf(task, Date.now()) },
      };
    }),

  tick: () => set({ nowMs: Date.now() }),

  clear: () => set({ running: {}, paused: {} }),
}));

/**
 * What a running task's clock reads: what it had accumulated, plus the wall clock
 * since the current run began.
 *
 * Never negative. A device whose clock is behind the server's would otherwise
 * show a count going backwards, and the shell corrects its clock from the
 * session on every read but not instantly.
 */
export function elapsedOf(task: RunningTask, nowMs: number): number {
  return task.accumulatedSeconds + Math.max(0, Math.round((nowMs - task.startedAt) / 1000));
}

/**
 * The live clock for one kit, or null when it is not running.
 *
 * Subscribed to the tick, so the component showing it re-renders once a second
 * and nothing else does.
 */
export function useElapsed(kitId: string | undefined): number | null {
  const task = useTimerStore((state) => (kitId === undefined ? undefined : state.running[kitId]));
  const nowMs = useTimerStore((state) => state.nowMs);
  return task === undefined ? null : elapsedOf(task, nowMs);
}

/** What a paused task's clock stopped at on this device, or null when unknown. */
export function usePausedSeconds(kitId: string | undefined): number | null {
  return useTimerStore((state) => (kitId === undefined ? null : (state.paused[kitId] ?? null)));
}

/** Every task running on this device, oldest run first, with its live clock. */
export function useRunningTasks(): { kitId: string; elapsedSeconds: number }[] {
  const running = useTimerStore((state) => state.running);
  const nowMs = useTimerStore((state) => state.nowMs);
  return Object.entries(running)
    .sort(([, a], [, b]) => a.startedAt - b.startedAt)
    .map(([kitId, task]) => ({ kitId, elapsedSeconds: elapsedOf(task, nowMs) }));
}

/** mm:ss, or h:mm:ss past an hour. From the prototype's fmt. */
export function formatElapsed(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return (
    (hours > 0 ? `${hours}:` : '') +
    String(minutes).padStart(hours > 0 ? 2 : 1, '0') +
    ':' +
    String(seconds).padStart(2, '0')
  );
}

/** Compact minutes and hours, for a duration that is read rather than watched. */
export function formatDuration(totalSeconds: number): string {
  const minutes = Math.round(totalSeconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

/**
 * The shared wall clock, for a component that displays elapsed time from an
 * instant rather than from one of this device's own tasks. One subscription to
 * the single application tick, section 10 item 4.
 */
export function useNow(): number {
  return useTimerStore((state) => state.nowMs);
}
